import React, { useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useTranslation } from "react-i18next";
import { format } from "date-fns";
import {
  History,
  X,
  Upload,
  ImageIcon,
  Trash2,
  Loader2,
  Plus,
  Search,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { AssessmentRecord } from "@/types/assessments";
import { extractTitle } from "@/lib/assessmentUtils";

import { cn } from "@/lib/utils";

export interface HistoryPanelProps {
  isOpen: boolean;
  onClose: () => void;
  items?: AssessmentRecord[];
  loading: boolean;
  selectedId: string;
  onSelect: (id: string) => void;
  onDelete: (id: string) => void;
  fetchNextPage?: () => void;
  hasNextPage?: boolean;
  isFetchingNextPage?: boolean;
}

/**
 * 历史记录面板 - 类似 Paste 的全屏底部磨玻璃设计
 */
export const HistoryPanel: React.FC<HistoryPanelProps> = ({
  isOpen,
  onClose,
  items,
  loading,
  selectedId,
  onSelect,
  onDelete,
  fetchNextPage,
  hasNextPage,
  isFetchingNextPage,
}) => {
  const { t } = useTranslation("assessments");
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  // 监听滚动以加载更多
  const handleScroll = () => {
    if (!scrollContainerRef.current || !hasNextPage || isFetchingNextPage)
      return;

    const { scrollLeft, scrollWidth, clientWidth } = scrollContainerRef.current;
    // 当滚动到右侧剩余不到 200px 时加载更多
    if (scrollWidth - scrollLeft - clientWidth < 200) {
      if (fetchNextPage) fetchNextPage();
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* 全屏遮罩 - 点击关闭 */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 z-40 bg-black/10"
            onClick={onClose}
          />

          {/* 底部面板 */}
          <motion.div
            initial={{ y: "100%", opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: "100%", opacity: 0 }}
            transition={{
              type: "spring",
              damping: 25,
              stiffness: 200,
              opacity: { duration: 0.2 },
            }}
            className="fixed inset-x-4 bottom-4 z-50 flex flex-col justify-end pointer-events-none sm:inset-x-8 sm:bottom-6"
          >
            {/* 内容容器 - 磨玻璃效果 - 悬浮+大圆角 */}
            <div
              className="pointer-events-auto relative w-full bg-background/60 backdrop-blur-xl border border-border/50 shadow-2xl rounded-3xl py-4 flex flex-col gap-4"
              onClick={(e) => e.stopPropagation()} // 防止点击面板关闭
            >
              {/* 标题栏 */}
              <div className="container mx-auto max-w-[1920px] px-4 sm:px-6 flex items-center justify-between">
                <div className="flex items-center gap-2.5 text-foreground">
                  <div className="bg-primary/10 p-1.5 rounded-md backdrop-blur-sm">
                    <History className="h-4 w-4 text-primary" />
                  </div>
                  <h3 className="font-medium text-sm sm:text-base tracking-tight">
                    {t("pipeline.history.title")}
                    <span className="ml-2 text-xs text-muted-foreground font-normal opacity-70">
                      {items?.length || 0} Records
                    </span>
                  </h3>
                </div>

                <Button
                  variant="ghost"
                  size="icon"
                  onClick={onClose}
                  className="h-8 w-8 rounded-full hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>

              {/* 横向滚动列表 */}
              <div
                ref={scrollContainerRef}
                onScroll={handleScroll}
                className="flex items-center gap-4 px-4 sm:px-6 overflow-x-auto pb-4 scrollbar-hide snap-x container mx-auto max-w-[1920px]"
                style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}
              >
                {/* 1. 新建卡片 */}
                <div className="flex-shrink-0 snap-start">
                  <div
                    className={cn(
                      "w-48 h-48 sm:w-56 sm:h-56 rounded-xl border flex flex-col items-center justify-center gap-4 cursor-pointer transition-all duration-300 group relative overflow-hidden aspect-square",
                      selectedId === "new"
                        ? "border-primary/30 bg-primary/5 shadow-none"
                        : "border-dashed border-zinc-800 bg-zinc-500/5 hover:border-zinc-700 hover:bg-zinc-500/10",
                    )}
                    onClick={() => {
                      onSelect("new");
                      onClose();
                    }}
                  >
                    {/* 背景装饰 - 使用 primary color */}
                    <div className="absolute inset-0 bg-gradient-to-br from-primary/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />

                    <div
                      className={cn(
                        "w-16 h-16 rounded-full flex items-center justify-center transition-transform duration-300 group-hover:scale-110",
                        selectedId === "new"
                          ? "bg-primary text-primary-foreground shadow-lg"
                          : "bg-muted text-muted-foreground",
                      )}
                    >
                      <Plus className="h-8 w-8" />
                    </div>

                    <div className="text-center z-10">
                      <p
                        className={cn(
                          "font-semibold text-lg",
                          selectedId === "new"
                            ? "text-primary"
                            : "text-foreground/80",
                        )}
                      >
                        {t("pipeline.history.newAnalysis")}
                      </p>
                      <p className="text-xs text-muted-foreground mt-1">
                        {t("pipeline.history.uploadNew")}
                      </p>
                    </div>
                  </div>
                </div>

                {/* 2. 列表内容 */}
                {loading && !items?.length
                  ? // Loading Skeletons
                    Array.from({ length: 4 }).map((_, idx) => (
                      <div key={idx} className="flex-shrink-0 snap-start">
                        <div className="w-48 h-48 sm:w-56 sm:h-56 rounded-xl border border-border bg-card/50 p-3 space-y-3 aspect-square">
                          <Skeleton className="w-full h-1/2 rounded-lg" />
                          <Skeleton className="w-3/4 h-4" />
                          <Skeleton className="w-1/2 h-3" />
                        </div>
                      </div>
                    ))
                  : items?.map((item) => {
                      const isSelected = selectedId === item.friendly_id;
                      const statusColor =
                        item.status === "completed"
                          ? "text-green-400 border-green-500/30 bg-green-500/10"
                          : item.status === "failed"
                            ? "text-red-400 border-red-500/30 bg-red-500/10"
                            : "text-blue-400 border-blue-500/30 bg-blue-500/10";

                      return (
                        <div
                          key={item.id}
                          className="flex-shrink-0 snap-start relative group"
                        >
                          {/* 删除按钮 - 悬浮显示 */}
                          <div className="absolute top-2 right-2 z-20 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                            <AlertDialog>
                              <AlertDialogTrigger asChild>
                                <Button
                                  variant="destructive"
                                  size="icon"
                                  className="h-8 w-8 rounded-full shadow-lg"
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </AlertDialogTrigger>
                              <AlertDialogContent
                                onClick={(e) => e.stopPropagation()}
                              >
                                <AlertDialogHeader>
                                  <AlertDialogTitle>
                                    {t("pipeline.history.deleteConfirm.title")}
                                  </AlertDialogTitle>
                                  <AlertDialogDescription>
                                    {t(
                                      "pipeline.history.deleteConfirm.description",
                                    )}
                                  </AlertDialogDescription>
                                </AlertDialogHeader>
                                <AlertDialogFooter>
                                  <AlertDialogCancel>
                                    {t("pipeline.history.deleteConfirm.cancel")}
                                  </AlertDialogCancel>
                                  <AlertDialogAction
                                    onClick={() => onDelete(item.friendly_id)}
                                    className="bg-destructive hover:bg-destructive/90"
                                  >
                                    {t(
                                      "pipeline.history.deleteConfirm.confirm",
                                    )}
                                  </AlertDialogAction>
                                </AlertDialogFooter>
                              </AlertDialogContent>
                            </AlertDialog>
                          </div>

                          {/* 卡片主体 */}
                          <div
                            className={cn(
                              "w-48 h-48 sm:w-56 sm:h-56 rounded-xl border flex flex-col overflow-hidden cursor-pointer transition-all duration-300 relative aspect-square",
                              isSelected
                                ? "border-primary shadow-lg ring-1 ring-primary/30 scale-[1.02] -translate-y-1"
                                : "border-border/40 bg-card/60 hover:bg-card/90 hover:border-border/80 hover:-translate-y-1",
                            )}
                            onClick={() => {
                              onSelect(item.friendly_id);
                              onClose();
                            }}
                          >
                            {/* 图片区域 */}
                            <div className="relative flex-1 bg-black/50 overflow-hidden">
                              {item.thumb_url || item.image_url ? (
                                <img
                                  src={item.thumb_url || item.image_url || ""}
                                  alt="Assessment"
                                  className="w-full h-full object-cover transition-all duration-700 group-hover:scale-105 opacity-0"
                                  loading="lazy"
                                  decoding="async"
                                  onLoad={(e) =>
                                    e.currentTarget.classList.remove(
                                      "opacity-0",
                                    )
                                  }
                                />
                              ) : (
                                <div className="w-full h-full flex items-center justify-center text-muted-foreground">
                                  <ImageIcon className="h-10 w-10 opacity-50" />
                                </div>
                              )}

                              {/* 渐变遮罩 */}
                              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent opacity-80" />

                              {/* 底部信息 - 叠加在图片上 */}
                              <div className="absolute bottom-0 left-0 right-0 p-3 pb-8">
                                {" "}
                                {/* pb-8 为下半部分留出过渡空间 */}
                              </div>
                            </div>

                            {/* 底部内容区 - 适配黑白模式 */}
                            {/* 底部内容区 - 高度缩减，紧凑布局 */}
                            <div className="bg-background/95 backdrop-blur-sm border-t border-border px-3 py-2 flex flex-col gap-0.5">
                              {/* 第一行：标题 + 状态点 */}
                              <div className="flex items-center justify-between gap-2">
                                <h4
                                  className="font-medium text-xs text-foreground truncate leading-tight flex-1"
                                  title={extractTitle(item.analysis_result)}
                                >
                                  {extractTitle(item.analysis_result)}
                                </h4>
                                {/* 弱化的状态指示器 - 仅显示圆点 */}
                                <div
                                  className={cn(
                                    "w-1.5 h-1.5 rounded-full flex-shrink-0",
                                    item.status === "completed"
                                      ? "bg-green-500"
                                      : item.status === "failed"
                                        ? "bg-red-500"
                                        : "bg-blue-500 animate-pulse",
                                  )}
                                  title={
                                    item.status === "completed"
                                      ? t("pipeline.history.status.completed")
                                      : item.status === "failed"
                                        ? t("pipeline.history.status.failed")
                                        : t(
                                            "pipeline.history.status.processing",
                                          )
                                  }
                                />
                              </div>

                              {/* 第二行：日期 + 鱼缸 */}
                              <div className="flex items-center justify-between">
                                <p className="text-[10px] text-muted-foreground">
                                  {format(
                                    new Date(item.created_at),
                                    "MM-dd HH:mm",
                                  )}
                                </p>
                                {item.tank_name && (
                                  <div className="text-[9px] text-muted-foreground px-1 py-px bg-muted rounded max-w-[80px] truncate">
                                    {item.tank_name}
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>

                          {/* 选中指示器 - 底部光点 */}
                          {isSelected && (
                            <div className="absolute -bottom-3 left-1/2 -translate-x-1/2 w-1.5 h-1.5 rounded-full bg-primary shadow-lg" />
                          )}
                        </div>
                      );
                    })}

                {/* 加载更多指示器 */}
                {hasNextPage && (
                  <div className="flex-shrink-0 snap-start flex items-center justify-center w-20 h-48 sm:h-56">
                    {isFetchingNextPage ? (
                      <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                    ) : (
                      <Button
                        variant="ghost"
                        className="h-full w-full text-muted-foreground/40 hover:text-muted-foreground hover:bg-muted/30 flex flex-col gap-2"
                        onClick={fetchNextPage}
                      >
                        <Search className="h-6 w-6" />
                        <span className="text-xs writing-mode-vertical">
                          {t("pipeline.history.loadMore")}
                        </span>
                      </Button>
                    )}
                  </div>
                )}
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};
